import * as React from "react";
import { Hand, Mail, Send } from "lucide-react";
import moment from "../../../utilities/Moment";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DashboardController } from "../../../controllers";
import ERoute from "../../../ERoute";
import RecurringScheduleHelper from "../../../helpers/RecurringScheduleHelper";
import RouteHelper from "../../../helpers/RouteHelper";
import {
  DashboardRecurringInvoiceDelivery,
  DashboardUpcomingRecurringInvoices,
} from "../../../types";
import NumberFormatter from "../../../utilities/NumberFormatter";
import CardEmptyInfo from "../../Card/CardEmptyInfo";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import { Badge } from "../../ui/badge";
import { buttonVariants } from "../../ui/button";
import { ToggleGroup, ToggleGroupItem } from "../../ui/toggle-group";

interface IProps {
  currency: string;
  numberFormat: string;
}

const PERIODS = [7, 30];

const SEGMENTED_GROUP = "gap-0.5 rounded-lg bg-muted p-0.5";
const SEGMENTED_ITEM =
  "h-7 min-w-0 rounded-md border border-transparent px-1.5 text-sm text-foreground/80 hover:bg-transparent aria-pressed:border-border aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-xs";

const UpcomingRecurringInvoicesWidget = (props: IProps) => {
  const { numberFormat } = props;
  const { t } = useTranslation();
  const [days, setDays] = React.useState(30);
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<DashboardUpcomingRecurringInvoices | null>(null);

  React.useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      setData(await DashboardController.getUpcomingRecurringInvoices(days));
    } catch (error) {
      console.error("Failed to fetch upcoming recurring invoices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderWhen = (date: string) => {
    const daysUntil = moment(date).startOf("day").diff(moment().startOf("day"), "days");

    return daysUntil === 1
      ? t("UpcomingRecurringInvoicesWidget::Tomorrow")
      : t("UpcomingRecurringInvoicesWidget::In {{count}} days", { count: daysUntil });
  };

  const renderDelivery = (delivery: DashboardRecurringInvoiceDelivery) => {
    switch (delivery) {
      case "peppol":
        return (
          <span className="inline-flex items-center gap-1">
            <Send className="size-3" />
            {t("UpcomingRecurringInvoicesWidget::Peppol")}
          </span>
        );
      case "email":
        return (
          <span className="inline-flex items-center gap-1">
            <Mail className="size-3" />
            {t("UpcomingRecurringInvoicesWidget::Email")}
          </span>
        );
      case "manual":
        return (
          <Badge variant="outline">
            <Hand data-icon="inline-start" />
            {t("UpcomingRecurringInvoicesWidget::Send manually")}
          </Badge>
        );
    }
  };

  const title = t("UpcomingRecurringInvoicesWidget::Upcoming recurring invoices");
  const viewAllLink = (
    <Link to={ERoute.PATH_RECURRING_INVOICES} className={buttonVariants({ variant: "link", size: "sm" })}>
      {t("UpcomingRecurringInvoicesWidget::View all recurring")}
    </Link>
  );

  return (
    <Panel
      className="h-full flex flex-col mb-0!"
      contentClasses="min-h-0 flex-1 flex flex-col p-0!"
      title={title}
      headerRight={viewAllLink}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-8 gap-y-2 border-b px-4 py-3">
        <div>
          <div className="text-xs text-muted-foreground">{t("UpcomingRecurringInvoicesWidget::Total to invoice")}</div>
          <div className="text-xl font-bold tabular-nums">
            {data ? NumberFormatter.formatCurrency(data.currency, numberFormat, Number(data.total)) : "-"}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{t("UpcomingRecurringInvoicesWidget::Invoices")}</div>
          <div className="text-xl font-bold tabular-nums">{data ? data.count : "-"}</div>
        </div>
        <ToggleGroup
          value={[String(days)]}
          onValueChange={(value: string[]) => value[0] && setDays(Number(value[0]))}
          size="sm"
          className={`ml-auto ${SEGMENTED_GROUP}`}
          aria-label={t("UpcomingRecurringInvoicesWidget::Period")}
        >
          {PERIODS.map((period) => (
            <ToggleGroupItem key={period} value={String(period)} className={SEGMENTED_ITEM}>
              {t("UpcomingRecurringInvoicesWidget::{{count}} days", { count: period })}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {isLoading || !data ? (
        <div className="min-h-0 flex-1">{isLoading ? <PageLoader /> : null}</div>
      ) : data.count === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <CardEmptyInfo
            icon="invoice"
            description={t("UpcomingRecurringInvoicesWidget::No recurring invoices in the next {{count}} days", {
              count: data.days,
            })}
          />
        </div>
      ) : (
        // On phones widgets take their natural height, so cap the list there and let it scroll
        <ul className="m-0 min-h-0 flex-1 list-none overflow-auto p-0 in-[.dashboard-mobile]:max-h-80">
          {data.invoices.map((invoice) => (
            <li key={`${invoice.id}-${invoice.date}`} className="border-b last:border-b-0">
              <Link
                to={RouteHelper.process(ERoute.PATH_INVOICE, { id: invoice.id })}
                className="flex items-center gap-3 px-4 py-2.5 text-foreground no-underline hover:bg-muted/50 hover:text-foreground"
              >
                <div className="w-12 shrink-0 text-center">
                  <div className="text-xs uppercase text-muted-foreground">{moment(invoice.date).format("MMM")}</div>
                  <div className="text-lg font-semibold leading-none tabular-nums">{moment(invoice.date).format("D")}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-medium">{invoice.contact_name || "-"}</span>
                    <span className="shrink-0 font-semibold tabular-nums">
                      {NumberFormatter.formatCurrency(invoice.currency, numberFormat, Number(invoice.amount))}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                    <span>{renderWhen(invoice.date)}</span>
                    <span>·</span>
                    <span>{RecurringScheduleHelper.recurrenceUnitLabel(invoice.recurrence_unit)}</span>
                    <span>·</span>
                    {renderDelivery(invoice.delivery)}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

export default UpcomingRecurringInvoicesWidget;
